import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { Update as UpdateIcon } from '@mui/icons-material';
import Select from '../../../components/Select';

/**
 * Update Unit Status Modal Component
 */
function UpdateStatusModal({ open, onClose, onConfirm, unitStatuses = [], unitData = null }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');

  // Populate status when modal opens
  useEffect(() => {
    if (unitData) {
      setStatus(unitData.status !== undefined ? unitData.status : '');
    } else {
      setStatus('');
    }
  }, [unitData, open]);

  // Handle confirm
  const handleConfirm = () => {
    if (unitData && status !== '') {
      onConfirm({
        id: unitData.id,
        status: typeof status === 'string' ? parseInt(status, 10) : status,
      });
    }
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    setStatus('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <UpdateIcon />
        {t('licenseUnits.updateStatus.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {unitData && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('licenseUnits.updateStatus.unitInfo', { code: unitData.code, name: unitData.name })}
            </Typography>
          )}
          <Select
            label={t('licenseUnits.updateStatus.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={unitStatuses}
            required
            id="status"
            name="status"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" id="cancel" name="cancel">
          {t('licenseUnits.updateStatus.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={status === ''}
          startIcon={<UpdateIcon />}
          id="confirm"
          name="confirm"
        >
          {t('licenseUnits.updateStatus.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UpdateStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  unitStatuses: PropTypes.array,
  unitData: PropTypes.object,
};

export default UpdateStatusModal;
